import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Button, FormControlLabel, Stack, Switch } from "@mui/material";
import apiClient from "api/apiClient";
import { setAuth } from "state/slices/authSlice";
import useMessageToast from "hooks/useMessageToast";

const API = process.env.REACT_APP_BASE_URL;

// The categories a user may switch off, in the order they appear.
//
// `account` mail — verification, password reset, organization invites — is deliberately
// absent and is not settable by the API either. Someone who switches off their own
// password reset is locked out of their account.
const CATEGORIES = [
  {
    key: "runs",
    label: "Run emails — when a simulation finishes or fails",
  },
  {
    key: "billing",
    label: "Billing emails — plan renewals and low credit balance",
  },
];

// Absent until the user saves this screen for the first time, and absent means OPTED IN.
// Reading it as "off" would show every existing user an unchecked box while they are in
// fact still receiving mail — the setting would then lie about the behaviour.
const prefOn = (user, key) => user?.notificationPrefs?.[key] !== false;

const CommunicationPreferencesTab = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  // Read to primitives up front. The re-sync effect below then depends on the VALUES
  // rather than on the user object, so a redux update that leaves the preferences
  // unchanged cannot wipe an edit the user has not saved yet.
  const initialOptIn = Boolean(user?.marketingOptIn);
  const initialRuns = prefOn(user, "runs");
  const initialBilling = prefOn(user, "billing");
  const initialPrefs = { runs: initialRuns, billing: initialBilling };

  const [marketingOptIn, setMarketingOptIn] = useState(initialOptIn);
  const [prefs, setPrefs] = useState(initialPrefs);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  useMessageToast(message, "success");
  const [error, setError] = useState("");

  useMessageToast(error, "error");
  useEffect(() => {
    setMarketingOptIn(initialOptIn);
    setPrefs({ runs: initialRuns, billing: initialBilling });
  }, [initialOptIn, initialRuns, initialBilling]);

  const hasChanges =
    marketingOptIn !== initialOptIn ||
    CATEGORIES.some(({ key }) => prefs[key] !== initialPrefs[key]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage("");
      setError("");

      const { data } = await apiClient.patch(
        `${API}/user/notification-preferences`,
        { marketingOptIn, notificationPrefs: prefs },
        { withCredentials: true },
      );

      dispatch(
        setAuth({
          isAuthenticated: true,
          user: {
            ...(user || {}),
            ...(data?.user || {}),
            marketingOptIn,
            notificationPrefs: { ...prefs },
          },
        }),
      );

      setMessage("Notification preferences updated.");
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to update notification preferences.");
    } finally {
      setSaving(false);
    }
  };

  // NOTE the direction of this one. The usual trap in this app is a white card under a
  // "dark" MUI palette, where unstyled text renders near-white on white. THIS card is
  // `.settings-section`, background #222b69 — genuinely dark — so the ink has to be LIGHT.
  // Giving it the dark ink the white-card rule prescribes is what made these three labels
  // unreadable on first attempt. Check the surface, do not apply the rule from memory.
  const switchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: "#00e676",
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#00e676",
      opacity: 1,
    },
  };

  return (
    <div className="settings-section">
      <h3>Notifications</h3>
      <p>
        Choose which emails you receive. Account and security emails — verifying your email,
        resetting your password, organization invites — are always sent and cannot be
        switched off.
      </p>

      <Stack
        spacing={2}
        mt={1}
        // Targets the label element itself: MUI renders it as its own Typography, so a
        // plain `color` on the container does not reach it.
        sx={{ "& .MuiFormControlLabel-label": { color: "#dfe5ff" } }}
      >
        {CATEGORIES.map(({ key, label }) => (
          <FormControlLabel
            key={key}
            control={(
              <Switch
                checked={prefs[key]}
                onChange={(e) => setPrefs((prev) => ({ ...prev, [key]: e.target.checked }))}
                sx={switchSx}
              />
            )}
            label={label}
          />
        ))}

        <FormControlLabel
          control={(
            <Switch
              checked={marketingOptIn}
              onChange={(e) => setMarketingOptIn(e.target.checked)}
              sx={switchSx}
            />
          )}
          label="Marketing emails — features, announcements, and offers"
        />

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          {saving ? "Saving..." : "Save Preferences"}
        </Button>

        {message ? <Alert severity="success">{message}</Alert> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
      </Stack>
    </div>
  );
};

export default CommunicationPreferencesTab;
